import { FC, memo } from 'react'
import { Box, useToken, useColorModeValue } from '@chakra-ui/react'

type Props = {
  size?: number | string
}

const EmptyDrawerIllustration: FC<Props> = ({ size = 60 }) => {
  const [primary100, primary300, primary500, primary600, primary700, gray50, gray900] = useToken('colors', [
    'primary.100',
    'primary.300',
    'primary.500',
    'primary.600',
    'primary.700',
    'gray.50',
    'gray.900',
  ])

  const bgColor = useColorModeValue(gray50, gray900)
  const strokeColor = useColorModeValue(primary500, primary700)
  const strokenWidth = '6px'
  const fillDrawer = useColorModeValue(primary100, primary300)
  const fillInnerDrawerLight = useColorModeValue(primary300, primary500)
  const fillInnerDrawerDark = useColorModeValue(primary600, primary600)

  return (
    <Box w={size} h={size} pos="relative">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 561.27 468.81">
        <g id="BG">
          <path
            fill={bgColor}
            d="M553.85,351.57,521.34,328.8l-4.78-3.34a232.19,232.19,0,0,0,17.89-89.65c0-128.69-104.31-233-233-233a232,232,0,0,0-107.1,26C181.91,20,169.66,10.62,156.76,2.53c-.2-.12-.38-.26-.57-.39-6.64-4.65-16.67-1.21-20.52,5.38C131.29,15,134.4,23.38,141.05,28l25.44,17.81a236.88,236.88,0,0,0-22.21,18L116.67,44.48c-13.24-9.27-26.21-19.36-39.91-27.95-.2-.12-.38-.26-.57-.39-6.64-4.65-16.67-1.21-20.52,5.38C51.29,29,54.4,37.38,61.05,42L93.73,64.92l29.59,20.72a235.49,235.49,0,0,0-16.44,22C96.72,100.29,86.59,92.73,76,86.11c-.19-.11-.37-.25-.55-.38-6.64-4.65-16.67-1.21-20.52,5.38-4.38,7.49-1.27,15.86,5.38,20.52l31.45,22,.18.12a232,232,0,0,0-23.5,102c0,128.68,104.32,233,233,233a232.28,232.28,0,0,0,166.76-70.3l18.18,12.73c13.19,9.24,26.11,19.28,39.76,27.84.19.12.37.26.56.39,6.64,4.65,16.67,1.21,20.53-5.38,4.37-7.48,1.27-15.86-5.39-20.52L509.34,390.8l-21.56-15.09a233.34,233.34,0,0,0,15.33-23.13c11.57,8.28,23,17,35,24.5.19.12.37.26.56.39,6.64,4.65,16.67,1.21,20.53-5.38C563.61,364.61,560.51,356.23,553.85,351.57Z"
          />
          <path
            fill={bgColor}
            d="M39.17,62.43,22.46,50.73c-6.45-4.51-17-1.38-20.52,5.38C-2.12,63.79.44,71.82,7.32,76.63L24,88.33C30.48,92.85,41,89.71,44.55,83,48.61,75.27,46.05,67.25,39.17,62.43Z"
          />
        </g>
        <g id="Stars">
          <g id="Star">
            <path fill={strokeColor} d="M529.45,136.31h16a1.5,1.5,0,0,0,0-3h-16a1.5,1.5,0,0,0,0,3Z" />
            <path fill={strokeColor} d="M536,126.81v16a1.5,1.5,0,0,0,3,0v-16a1.5,1.5,0,0,0-3,0Z" />
          </g>
          <g id="Star-2" data-name="Star">
            <path fill={strokeColor} d="M271.45,46.31h12a1.5,1.5,0,0,0,0-3h-12a1.5,1.5,0,0,0,0,3Z" />
            <path fill={strokeColor} d="M276,38.81v12a1.5,1.5,0,0,0,3,0v-12a1.5,1.5,0,0,0-3,0Z" />
          </g>
          <g id="Star-3" data-name="Star">
            <path fill={strokeColor} d="M492.45,286.31h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z" />
            <path fill={strokeColor} d="M496,279.81v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z" />
          </g>
          <g id="Star-4" data-name="Star">
            <path fill={strokeColor} d="M292.45,426.31h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z" />
            <path fill={strokeColor} d="M296,419.81v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z" />
          </g>
          <g id="Star-5" data-name="Star">
            <path fill={strokeColor} d="M102.45,376.31h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,1,0,0,3Z" />
            <path fill={strokeColor} d="M106,369.81v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z" />
          </g>
          <g id="Star-6" data-name="Star">
            <path fill={strokeColor} d="M48.45,256.31h18a1.5,1.5,0,0,0,0-3h-18a1.5,1.5,0,0,0,0,3Z" />
            <path fill={strokeColor} d="M56,245.81v18a1.5,1.5,0,0,0,3,0v-18a1.5,1.5,0,0,0-3,0Z" />
          </g>
          <g id="Star-7" data-name="Star">
            <path fill={strokeColor} d="M125.45,126.31h24a1.5,1.5,0,0,0,0-3h-24a1.5,1.5,0,0,0,0,3Z" />
            <path fill={strokeColor} d="M136,112.81v24a1.5,1.5,0,0,0,3,0v-24a1.5,1.5,0,0,0-3,0Z" />
          </g>
        </g>
        <g>
          <g>
            <polygon fill={fillInnerDrawerDark} points="423.67 182.31 423.67 236.31 446.95 236.31 423.67 182.31" />
            <polygon fill={fillInnerDrawerDark} points="195.24 182.31 195.24 236.31 171.95 236.31 195.24 182.31" />
            <rect fill={fillInnerDrawerLight} x="195.24" y="182.31" width="228.42" height="54" />
          </g>
          <polygon
            fill="none"
            stroke={strokeColor}
            strokeMiterlimit="10"
            strokeWidth={strokenWidth}
            points="451.45 242.81 167.45 241.81 194.45 180.81 425.45 181.81 451.45 242.81"
          />
          <g>
            <rect opacity="0.1" x="161.95" y="242.81" width="293" height="114.5" rx="12" />
            <rect
              fill={fillDrawer}
              stroke={strokeColor}
              strokeMiterlimit="10"
              strokeWidth={strokenWidth}
              x="164.45"
              y="237.81"
              width="289.5"
              height="114.5"
              rx="12"
            />
          </g>
          <g>
            <rect opacity="0.1" x="255.95" y="284.31" width="107" height="22" rx="11" />
            <rect fill={strokeColor} x="255.95" y="281.31" width="107" height="22" rx="11" />
          </g>
          <g>
            <path
              strokeLinecap="round"
              strokeDasharray="16"
              stroke={strokeColor}
              strokeMiterlimit="10"
              strokeWidth={strokenWidth}
              fill="none"
              d="M239,228.31s59-52,65-66c7.61-17.76-4-50-12-56s-18-13-38-12-41.44,20.31-45,39c-4,21,12.45,44.3,30,56,18,12,46,16,59,16s53-14,69-27,43-51,47-61,19-55.4,21-59"
            />
            <g>
              <circle fill={strokeColor} cx="446.43" cy="37.18" r="10.06" />
              <circle fill={strokeColor} cx="447.5" cy="49.2" r="10.06" />
              <circle fill={strokeColor} cx="434.76" cy="42.25" r="10.06" />
              <circle fill={strokeColor} cx="460.64" cy="37.94" r="13.08" />
              <circle fill={strokeColor} cx="441.35" cy="25.51" r="13.08" />
              <circle fill="#fff" cx="462.64" cy="37.76" r="4.02" />
              <circle fill="#fff" cx="441.35" cy="25.51" r="4.02" />
              <path
                fill={strokeColor}
                d="M455.75,14.06c.2,2.52-.61,5.15-1.33,7.53-.47,1.54-1,3.08-1.59,4.58l1.45.4a.66.66,0,0,1,0-.15c.09-.09-.09.19,0,0s.08-.17.13-.25a7.79,7.79,0,0,1,1-1.42,28.84,28.84,0,0,1,6.9-5.48c.82-.51.06-1.81-.76-1.3a29.53,29.53,0,0,0-7.26,5.8,6.47,6.47,0,0,0-1.49,2.39.75.75,0,0,0,1.45.4c.61-1.55,1.14-3.13,1.63-4.72.74-2.46,1.55-5.19,1.34-7.79-.07-1-1.57-1-1.5,0Z"
              />
            </g>
          </g>
        </g>
      </svg>
    </Box>
  )
}

export default memo(EmptyDrawerIllustration)
